import { supabase } from "@/integrations/supabase/client";
import { ApiCallsAuditService } from "@/services/apiCallsAuditService";
import { LLMConfigService } from "@/services/llm/LLMConfigService";
import { ApiTokensService } from "@/hooks/api-tokens/service";
import { UnifiedLLMService } from "@/services/llm/UnifiedLLMService";

interface GenerateContentParams {
  apiKey: string;
  model: string;
  prompt: string;
  source: string;
  brandId: string;
  temperature?: number;
  maxOutputTokens?: number;
  skipConfigCheck?: boolean; // Flag to skip config check when called from UnifiedLLMService
}

interface GeminiResponse {
  candidates: Array<{
    content: {
      parts: Array<{
        text: string;
      }>;
    };
  }>;
  usageMetadata?: {
    promptTokenCount: number;
    candidatesTokenCount: number;
    totalTokenCount: number;
  };
}

export class GeminiWrapper {
  async generateContent(params: GenerateContentParams): Promise<GeminiResponse> {
    const {
      apiKey: providedApiKey,
      model: providedModel,
      prompt,
      source,
      brandId,
      temperature: providedTemperature = 0.7,
      maxOutputTokens: providedMaxOutputTokens = 500
    } = params;

    // Check for LLM configuration for this source
    let finalModel = providedModel;
    let finalApiKey = providedApiKey;
    let finalTemperature = providedTemperature;
    let finalMaxOutputTokens = providedMaxOutputTokens;
    let configUsed = false;

    try {
      // Skip config check if called from UnifiedLLMService (to avoid infinite loops)
      if (params.skipConfigCheck) {
        console.log(`[GeminiWrapper] Skipping config check (called from UnifiedLLMService)`);
      } else {
        const config = await LLMConfigService.getLLMConfig(brandId, source);
        console.log(`[GeminiWrapper] Checking config for source: "${source}", brandId: "${brandId}"`);
        console.log(`[GeminiWrapper] Config lookup result:`, config ? {
          provider: config.provider,
          model: config.model,
          isActive: config.isActive,
          priority: config.priority
        } : 'No config found');
        
        if (config && config.isActive) {
          if (config.provider === 'gemini') {
          // Config exists for Gemini - use it
          finalModel = config.model;
          configUsed = true;
          
          // Get API key from tokens
          const apiTokens = await ApiTokensService.fetchTokens(brandId);
          if (apiTokens.gemini) {
            finalApiKey = apiTokens.gemini;
          }
          
          // Merge metadata from config
          if (config.metadata) {
            if (config.metadata.temperature !== undefined) {
              finalTemperature = providedTemperature ?? config.metadata.temperature;
            }
            if (config.metadata.maxOutputTokens !== undefined) {
              finalMaxOutputTokens = providedMaxOutputTokens ?? config.metadata.maxOutputTokens;
            }
          }
          
          console.log(`[GeminiWrapper] Using configured LLM for source "${source}": ${config.provider}/${config.model}`);
        } else {
          // Config exists but for a different provider - route through UnifiedLLMService
          console.log(`[GeminiWrapper] Config exists for source "${source}" with provider "${config.provider}", routing through UnifiedLLMService for provider switch`);
          
          // Convert prompt to UnifiedLLMService format and route
          const unifiedResponse = await UnifiedLLMService.callLLM({
            brandId,
            source,
            prompt,
            temperature: providedTemperature,
            maxOutputTokens: providedMaxOutputTokens
          });
          
          // Convert UnifiedLLMService response back to Gemini format
          return {
            candidates: [{
              content: {
                parts: [{
                  text: unifiedResponse.content
                }]
              }
            }],
            usageMetadata: unifiedResponse.usage ? {
              promptTokenCount: unifiedResponse.usage.prompt_tokens,
              candidatesTokenCount: unifiedResponse.usage.completion_tokens,
              totalTokenCount: unifiedResponse.usage.total_tokens
            } : undefined
          };
          }
        } else {
          console.log(`[GeminiWrapper] No active config found, using provided values: ${providedModel}`);
        }
      }
    } catch (error) {
      // If config lookup fails, fall back to provided values
      console.warn('[GeminiWrapper] ❌ Failed to fetch LLM config, using provided values:', error);
    }

    const startTime = Date.now();
    let statusCode = 0;
    let promptTokens = 0;
    let completionTokens = 0;
    let totalTokens = 0;
    let estimatedTokens = false;
    let responseData: GeminiResponse | null = null;
    let errorMessage: string | undefined;

    try {
      const requestBody = {
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: {
          temperature: finalTemperature,
          maxOutputTokens: finalMaxOutputTokens
        }
      };

      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/${finalModel}:generateContent?key=${finalApiKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }
      );

      statusCode = response.status;

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        errorMessage = errorData.error?.message || response.statusText;
        
        // Log the error and re-throw
        await this.logApiCall({
          source,
          brandId,
          model: finalModel,
          endpoint: `https://generativelanguage.googleapis.com/v1beta/models/${finalModel}:generateContent`,
          statusCode,
          duration: Date.now() - startTime,
          tokens: { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 },
          metadata: {
            error: errorMessage,
            request_failed: true,
            temperature: finalTemperature,
            maxOutputTokens: finalMaxOutputTokens,
            config_used: configUsed
          }
        });

        throw new Error(`Gemini API error (${statusCode}): ${errorMessage}`);
      }

      responseData = await response.json();

      // Extract token usage
      if (responseData.usageMetadata) {
        promptTokens = responseData.usageMetadata.promptTokenCount;
        completionTokens = responseData.usageMetadata.candidatesTokenCount;
        totalTokens = responseData.usageMetadata.totalTokenCount;
      } else {
        // Estimate tokens if not provided
        const content = responseData.candidates[0]?.content?.parts[0]?.text || '';
        completionTokens = Math.ceil(content.length / 4);
        promptTokens = Math.ceil(prompt.length / 4);
        totalTokens = promptTokens + completionTokens;
        estimatedTokens = true;
      }

      // Calculate cost
      const costEstimate = ApiCallsAuditService.estimateCost(
        'gemini',
        { prompt_tokens: promptTokens, completion_tokens: completionTokens, total_tokens: totalTokens },
        finalModel
      );

      // Log successful call
      console.log(`[GeminiWrapper] 📝 Logging API call to audit: platform=gemini, source="${source}", model="${finalModel}", config_used=${configUsed}`);
      await this.logApiCall({
        source,
        brandId,
        model: finalModel,
        endpoint: `https://generativelanguage.googleapis.com/v1beta/models/${finalModel}:generateContent`,
        statusCode,
        duration: Date.now() - startTime,
        tokens: {
          prompt_tokens: promptTokens,
          completion_tokens: completionTokens,
          total_tokens: totalTokens,
          estimated_tokens: estimatedTokens
        },
        costEstimate,
        metadata: {
          temperature: finalTemperature,
          maxOutputTokens: finalMaxOutputTokens,
          estimated_tokens: estimatedTokens,
          config_used: configUsed
        }
      });

      return responseData;

    } catch (error) {
      // If we haven't logged yet, log the error
      if (!errorMessage) {
        await this.logApiCall({
          source,
          brandId,
          model: finalModel,
          endpoint: `https://generativelanguage.googleapis.com/v1beta/models/${finalModel}:generateContent`,
          statusCode: statusCode || 0,
          duration: Date.now() - startTime,
          tokens: { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 },
          metadata: {
            error: error instanceof Error ? error.message : 'Unknown error',
            request_failed: true,
            temperature: finalTemperature,
            maxOutputTokens: finalMaxOutputTokens,
            config_used: configUsed
          }
        });
      }

      throw error;
    }
  }

  private async logApiCall(data: {
    source: string;
    brandId: string;
    model: string;
    endpoint: string;
    statusCode: number;
    duration: number;
    tokens: {
      prompt_tokens: number;
      completion_tokens: number;
      total_tokens: number;
      estimated_tokens?: boolean;
    };
    costEstimate?: number;
    metadata?: any;
  }) {
    try {
      const { data: userData } = await supabase.auth.getUser();
      
      await ApiCallsAuditService.logApiCall({
        source_menu_item: data.source,
        platform: 'gemini',
        endpoint: data.endpoint,
        method: 'POST',
        status_code: data.statusCode,
        duration_ms: data.duration,
        cost_estimate: data.costEstimate || 0,
        brand_id: data.brandId,
        model_used: data.model,
        metadata: {
          ...data.metadata,
          prompt_tokens: data.tokens.prompt_tokens,
          completion_tokens: data.tokens.completion_tokens,
          total_tokens: data.tokens.total_tokens
        }
      });
    } catch (error) {
      console.error('Failed to log API call:', error);
    }
  }
}
